// packages/web-ui/src/AppShell/KeyboardSafeTooltip.tsx
//
// W2-S3 AC #7 retrofit (per UX §4.4): a small Radix-equivalent tooltip wrapper
// that satisfies all four UX §4.4 contract points:
//
//   1. Appears on hover after a 600 ms delay (Radix `<Tooltip>` default).
//   2. Tooltip body shows the button label + the keyboard shortcut in a
//      `<sub>` subscript.
//   3. Skips on keyboard focus — the trigger's existing `aria-label` already
//      announces the destination + shortcut, so SCB convention dictates that
//      keyboard-focused users do not also see the visual tooltip.
//   4. Additive only — does not strip the trigger's existing `aria-label`,
//      `aria-pressed`, `title` (caller-owned), or focus ring.
//
// Implementation choices (vs. importing `@base-ui/react`'s `<Tooltip>`):
//   - The story Dev Notes (line 408) explicitly bless a "5-LOC inline
//     equivalent rather than full shadcn lift" path while the project's
//     `@base-ui/react` adoption is still partial. Other AppShell primitives
//     (HelpPopover, DeleteConfirmDialog, DragHandle) follow the same
//     hand-rolled pattern.
//   - jsdom does not run floating-ui's positioning / portal layers reliably,
//     so an inline absolutely-positioned popup keeps the W2-S3 test suite
//     deterministic without portal stubs.
//
// Skip-on-keyboard-focus mechanism:
//   - We track the user's last input modality at module scope via a single
//     pair of document listeners (keydown → keyboard, pointerdown / pointermove
//     / mousedown → pointer). The Tab key explicitly flips modality back to
//     keyboard so a Tab-into-the-button suppresses the tooltip even after a
//     prior mouse interaction.
//   - On `focus`, we open the tooltip ONLY when the most recent modality was
//     pointer (matching the SCB `[data-pointer]` convention referenced in the
//     UX spec). Hover-driven open continues to fire after the 600 ms delay.

import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  CSSProperties,
  FocusEvent as ReactFocusEvent,
  JSX,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  Ref,
} from 'react';

/** Radix `<TooltipProvider>` default delay; matches UX §4.4 line 280. */
export const KEYBOARD_SAFE_TOOLTIP_DELAY_MS = 600;

// ─────────────────────────────────────────────────────────────────────────────
// Module-scope input modality tracker.
// ─────────────────────────────────────────────────────────────────────────────

type InputModality = 'keyboard' | 'pointer';

let currentModality: InputModality = 'pointer';
let listenersInstalled = false;

function ensureModalityListeners(): void {
  if (listenersInstalled) return;
  if (typeof document === 'undefined') return;
  listenersInstalled = true;

  const onKeyDown = (event: KeyboardEvent): void => {
    // Modifier-only keys (Shift/Ctrl/Alt/Meta) and printable characters all
    // count as keyboard input; treat any keydown as a keyboard signal so a
    // Tab-into-button reliably flips modality back from pointer.
    if (event.metaKey && event.key !== 'Tab') {
      // Cmd+click on macOS still uses the pointer; ignore Cmd-only events.
      return;
    }
    currentModality = 'keyboard';
  };
  const onPointer = (): void => {
    currentModality = 'pointer';
  };

  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('pointerdown', onPointer, true);
  document.addEventListener('mousedown', onPointer, true);
  document.addEventListener('touchstart', onPointer, true);
}

/** Test-only: read the current modality. */
export function _getKeyboardSafeTooltipModalityForTests(): InputModality {
  return currentModality;
}

/** Test-only: forcibly set the modality. */
export function _setKeyboardSafeTooltipModalityForTests(value: InputModality): void {
  currentModality = value;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public component
// ─────────────────────────────────────────────────────────────────────────────

export interface KeyboardSafeTooltipProps {
  /** Button label rendered as the primary tooltip content. */
  label: string;
  /**
   * Keyboard shortcut displayed in `<sub>` subscript next to the label.
   * Optional — omit for buttons without a shortcut (e.g. the disabled Account
   * placeholder).
   */
  shortcut?: string;
  /**
   * Override the open delay. Defaults to 600 ms per UX §4.4. Tests pass `0`
   * to flush the timer synchronously.
   */
  delayMs?: number;
  /**
   * The trigger element (typically a `<button>`). Receives merged event
   * handlers and an `aria-describedby` pointer to the tooltip. Existing
   * handlers/aria are preserved.
   */
  children: ReactElement<TriggerElementProps>;
}

/** Subset of element props we splice into the trigger via `cloneElement`. */
interface TriggerElementProps {
  onPointerEnter?: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerLeave?: (event: ReactPointerEvent<HTMLElement>) => void;
  onFocus?: (event: ReactFocusEvent<HTMLElement>) => void;
  onBlur?: (event: ReactFocusEvent<HTMLElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  'aria-describedby'?: string;
  ref?: Ref<HTMLElement>;
}

/**
 * Wrap an interactive trigger to render a tooltip with the UX §4.4 contract.
 *
 * @example
 *   <KeyboardSafeTooltip label="Chat" shortcut="Ctrl+1">
 *     <button aria-label="Chat">…</button>
 *   </KeyboardSafeTooltip>
 */
export function KeyboardSafeTooltip({
  label,
  shortcut,
  delayMs = KEYBOARD_SAFE_TOOLTIP_DELAY_MS,
  children,
}: KeyboardSafeTooltipProps): JSX.Element {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    ensureModalityListeners();
  }, []);

  // Cleanup the pending timer on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const clearTimer = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handlePointerEnter = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      // Only react to mouse / pen — touch should not trigger a hover tooltip.
      if (event.pointerType === 'touch') return;
      clearTimer();
      if (delayMs <= 0) {
        setOpen(true);
        return;
      }
      timerRef.current = setTimeout(() => {
        setOpen(true);
        timerRef.current = null;
      }, delayMs);
    },
    [clearTimer, delayMs],
  );

  const handlePointerLeave = useCallback((): void => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  const handleFocus = useCallback((): void => {
    // Skip on keyboard focus per UX §4.4 line 282 — `aria-label` already
    // announces the destination for AT users.
    if (currentModality === 'keyboard') return;
    // Pointer-driven focus (e.g. mouse-down on a button) should still show the
    // tooltip immediately for parity with the hover-open behaviour.
    setOpen(true);
  }, []);

  const handleBlur = useCallback((): void => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      // Click should immediately dismiss the tooltip — a navigation /
      // activation should not leave the popup hanging over the canvas.
      clearTimer();
      setOpen(false);
      // We intentionally do not call event.preventDefault(); the trigger's
      // own onClick (e.g. navigateTo) still fires via cloneElement merge.
      void event;
    },
    [clearTimer],
  );

  // Merge our handlers with any handlers the caller already attached. The
  // wrapped element's existing aria-label / aria-pressed / title remain on
  // the trigger untouched (cloneElement only adds the props we list below).
  const trigger = isValidElement(children) ? children : null;
  if (trigger === null) {
    throw new Error('KeyboardSafeTooltip: children must be a single React element');
  }

  const existingProps = trigger.props;
  const mergedProps: TriggerElementProps = {
    onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => {
      existingProps.onPointerEnter?.(event);
      handlePointerEnter(event);
    },
    onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => {
      existingProps.onPointerLeave?.(event);
      handlePointerLeave();
    },
    onFocus: (event: ReactFocusEvent<HTMLElement>) => {
      existingProps.onFocus?.(event);
      handleFocus();
    },
    onBlur: (event: ReactFocusEvent<HTMLElement>) => {
      existingProps.onBlur?.(event);
      handleBlur();
    },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      existingProps.onClick?.(event);
      handleClick(event);
    },
    // `aria-describedby` chains rather than replaces — preserves any existing
    // describedby (e.g. set by a parent for an error popover).
    'aria-describedby': existingProps['aria-describedby']
      ? `${existingProps['aria-describedby']} ${tooltipId}`
      : tooltipId,
  };

  // Tooltip popup is a sibling positioned relative to a `display: contents`
  // wrapper so the trigger keeps its layout participation. We use plain
  // absolute positioning anchored at the wrapper; per UX §4.1 the
  // ActivityBar buttons are 36 px so a fixed offset is good enough.
  const popupStyle: CSSProperties = useMemo(
    () => ({
      position: 'absolute',
      top: '50%',
      left: 'calc(100% + 8px)',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      zIndex: 50,
    }),
    [],
  );

  const cloned = cloneElement(trigger, mergedProps);

  return (
    <span className="relative inline-flex" data-keyboard-safe-tooltip-wrapper="">
      {cloned}
      {open ? (
        <span
          role="tooltip"
          id={tooltipId}
          data-testid={`keyboard-safe-tooltip-${tooltipId}`}
          data-keyboard-safe-tooltip-popup=""
          style={popupStyle}
          className="border-taupe-500 bg-cream-50 text-fg rounded-none border px-2 py-1 text-xs"
        >
          <span data-keyboard-safe-tooltip-label="">{label}</span>
          {shortcut !== undefined ? (
            <>
              {' '}
              <sub
                data-keyboard-safe-tooltip-shortcut=""
                className="text-[10px] text-taupe-700"
              >
                {shortcut}
              </sub>
            </>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
